import { FirebaseServices } from "./config";
import { callFunction } from "./helpers";
import { entityCache } from "../sync/entity-cache";
import {
  CreateAnchorRequest,
  CreateAnchorResponse,
  EditAnchorRequest,
  EditAnchorResponse,
  DeleteAnchorRequest,
  DeleteAnchorResponse,
  GetAnchorRequest,
  GetAnchorResponse,
  GetMyAnchorsRequest,
  GetMyAnchorsResponse,
  GetFilteredAnchorsRequest,
  GetFilteredAnchorsResponse,
  GetAllAnchorsRequest,
  GetAllAnchorsResponse,
} from "common247";

/**
 * Creates a new Anchor entity.
 *
 * @param fb - Firebase services instance.
 * @param request - Payload with name, type, description, address, coordinates, etc.
 * @returns Server response with created anchorId.
 */
export async function createAnchor(
  fb: FirebaseServices,
  request: CreateAnchorRequest
): Promise<CreateAnchorResponse> {
  const result = (await callFunction(fb, "createAnchorFunc", request)) as CreateAnchorResponse;
  return result;
}

/**
 * Modifies an existing Anchor entity.
 *
 * @param fb - Firebase services instance.
 * @param request - Payload with anchorId and mutated attributes.
 * @returns Standard server response.
 */
export async function editAnchor(
  fb: FirebaseServices,
  request: EditAnchorRequest
): Promise<EditAnchorResponse> {
  const result = (await callFunction(fb, "editAnchorFunc", request)) as EditAnchorResponse;
  return result;
}

/**
 * Deletes an Anchor entity.
 *
 * @param fb - Firebase services instance.
 * @param request - Payload with anchorId.
 * @returns Standard server response.
 */
export async function deleteAnchor(
  fb: FirebaseServices,
  request: DeleteAnchorRequest
): Promise<DeleteAnchorResponse> {
  const result = (await callFunction(fb, "deleteAnchorFunc", request)) as DeleteAnchorResponse;
  return result;
}

/**
 * Retrieves a single Anchor entity by ID.
 *
 * @param fb - Firebase services instance.
 * @param request - Payload with anchorId.
 * @returns Server response with anchor document.
 */
export async function getAnchor(
  fb: FirebaseServices,
  request: GetAnchorRequest
): Promise<GetAnchorResponse> {
  const result = (await callFunction(fb, "getAnchorFunc", request)) as GetAnchorResponse;
  return result;
}

/**
 * Retrieves all anchors owned by or shared with the caller.
 *
 * @param fb - Firebase services instance.
 * @param request - Optional query options.
 * @returns Server response with anchors array.
 */
export async function getMyAnchors(
  fb: FirebaseServices,
  request: GetMyAnchorsRequest
): Promise<GetMyAnchorsResponse> {
  const result = (await callFunction(fb, "getMyAnchorsFunc", request)) as GetMyAnchorsResponse;
  if (result && Array.isArray(result.anchors)) {
    entityCache.setCachedList("anchor" as any, result.anchors, "myAnchors");
  }
  return result;
}

/**
 * Retrieves filtered anchors matching unified search criteria.
 *
 * @param fb - Firebase services instance.
 * @param request - Filter query options.
 * @returns Filtered anchor response with counts.
 */
export async function getFilteredAnchors(
  fb: FirebaseServices,
  request: GetFilteredAnchorsRequest
): Promise<GetFilteredAnchorsResponse> {
  const result = (await callFunction(fb, "getFilteredAnchorsFunc", request)) as GetFilteredAnchorsResponse;
  return result;
}

/**
 * Retrieves all anchors across the system (admin operation).
 *
 * @param fb - Firebase services instance.
 * @param request - Query options.
 * @returns All anchors array.
 */
export async function getAllAnchors(
  fb: FirebaseServices,
  request: GetAllAnchorsRequest
): Promise<GetAllAnchorsResponse> {
  const result = (await callFunction(fb, "getAllAnchorsFunc", request)) as GetAllAnchorsResponse;
  return result;
}
